import { Check, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import milestoneIcon from "@/assets/milestone-icon.png";
import type { Milestone } from "./utils";

interface MilestoneItemProps {
  milestone: Milestone;
  isVisible: boolean;
  isMobile?: boolean;
}

export const MilestoneItem = ({ milestone, isVisible, isMobile = false }: MilestoneItemProps) => {
  const isCompleted = milestone.id <= 2;
  const isHighlighted = milestone.id === 3;

  const circleClasses = cn(
    "w-12 h-12 rounded-full border-4 flex items-center justify-center transition-all duration-500",
    isHighlighted
      ? "border-blue-500 shadow-[0_0_30px_rgba(59,130,246,0.8)]"
      : "bg-green-500 border-green-500 shadow-[0_0_30px_rgba(34,197,94,0.8)]",
    isMobile && isHighlighted && "shadow-[0_0_20px_rgba(59,130,246,0.8)]",
    isMobile && !isHighlighted && "shadow-[0_0_20px_rgba(34,197,94,0.8)]",
    isVisible ? "scale-100 opacity-100" : "scale-0 opacity-0"
  );

  const circleStyle = isHighlighted
    ? { backgroundImage: `url(${milestoneIcon})`, backgroundSize: "cover" }
    : {};

  const badgeClasses = cn(
    "text-xs font-bold rounded-full",
    isMobile ? "px-2 py-1" : "px-3 py-1",
    milestone.highlighted
      ? "bg-primary/30 text-primary-foreground"
      : "bg-white/10 text-white/70"
  );

  const titleClasses = cn(
    "font-bold text-white",
    isMobile ? "text-xl my-3" : "text-2xl my-4"
  );

  const descriptionClasses = cn(
    "text-white/80",
    isMobile && "text-sm"
  );

  const containerClasses = cn(
    isMobile ? "flex items-start gap-6" : "relative",
    "transition-all duration-600",
    isVisible 
      ? "opacity-100 translate-y-0 translate-x-0" 
      : isMobile 
        ? "opacity-0 -translate-x-8" 
        : "opacity-0 translate-y-8"
  );

  return (
    <div className={containerClasses}>
      {isMobile ? (
        <>
          <div
            className={circleClasses}
            style={circleStyle}
          >
            {isCompleted && <Check className="w-6 h-6 text-white" />}
          </div>
          <div className="flex-1 pt-1">
            <span className={badgeClasses}>Step {milestone.id}</span>
            <h3 className={titleClasses}>{milestone.title}</h3>
            <p className={descriptionClasses}>{milestone.description}</p>
            {milestone.highlighted && (
              <div className="mt-3 inline-flex items-center gap-2 bg-primary/30 px-3 py-1.5 rounded-full border border-primary/50">
                <Zap className="w-3 h-3 text-primary-foreground" />
                <span className="text-xs font-bold text-primary-foreground">
                  GlobalCoders Role
                </span>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-center mb-8">
            <div
              className={circleClasses}
              style={circleStyle}
            >
              {isCompleted && <Check className="w-6 h-6 text-white" />}
            </div>
          </div>
          <div className="text-center">
            <span className={badgeClasses}>Step {milestone.id}</span>
            <h3 className={titleClasses}>{milestone.title}</h3>
            <p className={descriptionClasses}>{milestone.description}</p>
            {milestone.highlighted && (
              <div className="mt-4 inline-flex items-center gap-2 bg-primary/30 px-4 py-2 rounded-full border border-primary/50">
                <Zap className="w-4 h-4 text-primary-foreground" />
                <span className="text-sm font-bold text-primary-foreground">
                  GlobalCoders Role
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
